// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import XngZone from "@plumberssupply/App/Components/Common/XngZone";
import { orderWrapperGridItem } from "@plumberssupply/App/WidgetExtensions/MyAccountPagesSharedStyles/OrderInformationStyle";
import {
    avoidBreakInsidePrintElement,
    color,
    displayBlockOnPrint,
    hideElementOnPrint,
    xScreen,
} from "@plumberssupply/XDL/Utilities";
import React from "react";
import styled, { css } from "styled-components";

const enum fields {
    showHeaderActionSection = "showHeaderActionSection",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.showHeaderActionSection]: boolean;
    };
}

export interface MyAccountDetailsPagesContainerStyles {
    mainWrapper?: InjectableCss;
    headerContainer?: GridContainerProps;
    headerGridItem?: GridItemProps;
    contentContainer?: GridContainerProps;
    leftColumnGridItem?: GridItemProps;
    rightColumnGridItem?: GridItemProps;
    lineListingGridItem?: GridItemProps;
    titleGridItem?: GridItemProps;
    actionsGridItem?: GridItemProps;
    content?: InjectableCss;
    informationWrapper?: InjectableCss;
    shippingBillingWrapper?: InjectableCss;
    title?: TypographyPresentationProps;
    extraInfoContainer?: InjectableCss;
    extraInfoContainerPrint?: InjectableCss;
}

export const myAccountDetailsPagesContainerStyles: MyAccountDetailsPagesContainerStyles = {
    mainWrapper: {
        css: css`
            ${displayBlockOnPrint}
        `,
    },
    headerContainer: {
        gap: 10,
        offsetCss: css`
            ${displayBlockOnPrint}
        `,
        css: css`
            ${displayBlockOnPrint}
        `,
    },
    headerGridItem: {
        width: 12,
        printWidth: 12,
        css: css`
            ${displayBlockOnPrint}
        `,
    },
    contentContainer: {
        gap: 20,
        offsetCss: css`
            &&& {
                ${xScreen("lte", "sm")} {
                    flex-direction: column-reverse;
                }
                @media print {
                    display: flex !important; // overrides inline style for print
                    flex-direction: row;
                }
            }
        `,
        css: css`
            ${displayBlockOnPrint}
        `,
    },
    leftColumnGridItem: {
        width: [12, 8, 8, 9, 9],
        printWidth: 12,
        css: css`
            flex-direction: column;
            ${xScreen("lte", "sm")} {
                max-width: initial;
            }
            @media print {
                max-width: initial;
                display: block;
            }
        `,
    },
    rightColumnGridItem: {
        width: [12, 4, 4, 3, 3],
        printWidth: 12,
        css: css`
            ${xScreen("lte", "sm")} {
                max-width: initial;
            }
            @media print {
                max-width: initial;
                display: block;
            }
        `,
    },
    lineListingGridItem: { width: 12 },
    titleGridItem: {
        width: [10, 10, 8, 5, 5],
        css: css`
            ${displayBlockOnPrint}
        `,
    },
    actionsGridItem: {
        width: [2, 2, 4, 7, 7],
        css: css`
            ${hideElementOnPrint}
        `,
    },
    content: {
        css: css`
            width: 100%;
            ${displayBlockOnPrint}
        `,
    },
    informationWrapper: {
        css: css`
            width: 100%;
            margin-top: 20px;
            & > div[class^="Wrapper"] {
                display: flex;
                flex-wrap: wrap;
                width: 100%;
                &:empty {
                    display: none;
                }
                @media print {
                    display: block;
                    width: unset;
                    > div {
                        display: flex;
                        flex-direction: row;
                        margin-bottom: 5px;
                        align-items: baseline;
                        width: unset;
                        > h2, h3, h6, span {
                            font-size: 12px;
                        }
                    }
                    display: grid;
                    grid-template-columns: 1fr 1fr;
                }
            }

            // To fix ShellView issue when add zone widget wrapper
            // the code will apply in Order details page with ShellView mode only.
            &&&& > div[class^="Wrapper"] div[data-widget^="OrderDetails"]:not(:empty) {
                & > div {
                    ${orderWrapperGridItem}

                    width: 100%;
                }
            }
        `,
    },
    shippingBillingWrapper: {
        css: css`
            width: 100%;
            display: flex;
            flex-wrap: wrap;
            @media print {
                width: 100%;
                display: flex;
            }
            & > div[class^="Wrapper"] {
                display: flex;
                flex-wrap: wrap;

                &:empty {
                    display: none;
                }
                width: 50%;
                ${xScreen("lte", "sm")} {
                    width: 100%;
                }
                @media print {
                    width: 50%;
                    display: block;
                    h5 {
                        font-size: 14px;
                    }
                    p {
                        font-size: 12px;
                    }
                }
            }
        `,
    },
    title: {
        color: "common.ui11",
        weight: 800,
        size: 18,
        forwardAs: "h2",
    },
    extraInfoContainer: {
        css: css`
            @media print {
                ${({ isOrderDetailsPage }: any) => (isOrderDetailsPage ? "display: none;" : "")}
            }
        `,
    },
    extraInfoContainerPrint: {
        css: css`
            display: none;
            @media print {
                display: block;
            }
        `,
    },
};

const Separator = styled.hr`
    border: none;
    border-bottom: 1px solid ${color("common.ui7")};
    margin: 11px 0 31px 0;
    width: 100%;
`;

const styles = myAccountDetailsPagesContainerStyles;

type Props = OwnProps;

const MyAccountDetailsPagesContainer = ({ id, fields: { showHeaderActionSection } }: Props) => {
    const isOrderDetailsPage = useAppSelector(state => getCurrentPage(state).type === "OrderDetailsPage");

    return (
        <StyledWrapper {...styles.mainWrapper}>
            <GridContainer {...styles.headerContainer}>
                {!showHeaderActionSection ? (
                    <GridItem {...styles.headerGridItem}>
                        <XngZone zoneName="Content00" contentId={id} hint="Zone For Page Header" />
                    </GridItem>
                ) : (
                    <>
                        <GridItem {...styles.titleGridItem}>
                            <XngZone zoneName="Content00" contentId={id} hint="Zone For Page Title" />
                        </GridItem>
                        <GridItem {...styles.actionsGridItem}>
                            <XngZone zoneName="Content01" contentId={id} hint="Zone For Header and Action Buttons" />
                        </GridItem>
                    </>
                )}
            </GridContainer>
            <GridContainer {...styles.contentContainer}>
                <GridItem {...styles.leftColumnGridItem}>
                    <StyledWrapper {...styles.content}>
                        <Typography {...styles.title}>{translate("Order Information")}</Typography>
                        <StyledWrapper {...styles.informationWrapper}>
                            <XngZone zoneName="Content02" contentId={id} hint="Zone For Details Information" />
                            <Separator />
                        </StyledWrapper>
                        <Typography {...styles.title}>{translate("Addresses")}</Typography>
                        <StyledWrapper {...styles.shippingBillingWrapper}>
                            <XngZone zoneName="Content03" contentId={id} hint="Zone For Details Billing Information" />
                            <XngZone zoneName="Content04" contentId={id} hint="Zone For Details Shipping Information" />
                            <Separator />
                        </StyledWrapper>
                        <StyledWrapper {...styles.extraInfoContainer} {...{isOrderDetailsPage}}>
                            <XngZone zoneName="Content05" contentId={id} hint="Zone For Details Extra Information" />
                        </StyledWrapper>
                    </StyledWrapper>
                </GridItem>
                <GridItem {...styles.rightColumnGridItem}>
                    <XngZone zoneName="Content06" contentId={id} hint="Zone For Order Summary" />
                </GridItem>
            </GridContainer>
            {isOrderDetailsPage && (
                <StyledWrapper {...styles.extraInfoContainerPrint}>
                    <XngZone zoneName="Content05" contentId={id} hint="Zone For Details Extra Information" />
                </StyledWrapper>
            )}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: MyAccountDetailsPagesContainer,
    definition: {
        group: "Xngage Custom" as any,
        displayName: "My Account Details Pages Container",
        fieldDefinitions: [
            {
                name: fields.showHeaderActionSection,
                displayName: "Show header action section",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;
